import { Dispatch, SetStateAction } from 'react'
import { Translate } from 'next-translate'
import router from 'next/router'
import { ParsedUrlQuery } from 'querystring'
import { ValueType } from 'rsuite/esm/Radio'

import useCategoryDataStructure from '../components/hooks/useCategoryDataStructure'
import { capitalizeFirstLetter } from '../utils/util'
import { getCatalogName } from './category'
import { isGerman } from '../utils/translations'
import { CatalogType, Filter, FiltersType, isDefined } from '../utils/types/misc'
import { DataItemType } from '../utils/types/rsuite'
import { Catalog, SortOptions } from '../utils/types/Product'
import { QueryParam } from '../utils/constants'

export const getMainHeading = (
  catalogTab: CatalogType,
  query: ParsedUrlQuery,
  t: Translate,
  lang: string,
  categories: Catalog[] = [], // labels already translated
) => {
  const tab = t(capitalizeFirstLetter(catalogTab))
  const { selectedCategory: category } = useCategoryDataStructure()
  const mainCategoryName = getCatalogName(category, categories, t)
  const searchQuery = query[QueryParam.search]
  if (searchQuery) {
    return capitalizeFirstLetter(`${t('{{ tab }} matching',
      { tab: tab.toLowerCase() })} "${searchQuery}"`)
  }
  const categoryText = t(mainCategoryName || '')

  if (categoryText) return categoryText

  return `${catalogTab === 'products' ? `${t('Featured')} ` : ''}${isGerman(lang) || catalogTab !== 'products' ? tab : tab.toLowerCase()}`
}

export const resetFilters = (
  filters: FiltersType[],
  setFilters: Dispatch<SetStateAction<FiltersType[]>>,
  catalogTab: 'Products' | 'Suppliers',
  pushT: typeof router.push,
  query: ParsedUrlQuery,
) => (clearAll = false) => {
  const searchQuery = query[QueryParam.search]
  const searchQueryString = searchQuery ? `?${QueryParam.search}=${searchQuery}` : ''
  const selectedSupplier = query[QueryParam.supplierCode] as string
  const redirect = catalogTab.toLowerCase()
  // for clearing the catalog from URL on pressing reset filters button
  if (clearAll && filters.find(({ filter }) => filter === 'catalog')) {
    if (!selectedSupplier) { // disable route-based category in supplier store page
      pushT(`/${redirect}${searchQueryString}`, undefined, { shallow: true })
    }
    setFilters([])
  } else {
    // for clearing all the filters except catalog
    setFilters((prevFilters) => prevFilters.filter((item) => item.filter === 'catalog'))
  }
}

export const filterChange = (
  filters: FiltersType[],
  setFilters: Dispatch<SetStateAction<FiltersType[]>>,
) => (filter: Filter, activeFilters: ValueType[]) => {
  const removeFilter = filters.filter((ele) => ele.filter !== filter)
  if (!activeFilters
    || activeFilters.length === 0
    || (filter === 'delivery' && activeFilters[0] === '28') // 28 days(4 weeks)
    || (filter === 'mov' && activeFilters[0] === '-1')) {
    // No need to set empty filter
    setFilters([...removeFilter])
  } else {
    setFilters([...removeFilter, { filter, activeFilters }])
  }
}

export const isBottomCategory = (item: Catalog): boolean => (
  isDefined(item['catalog.level']) && item['catalog.level'] > 3
)

export const createItemType = (id: string, list: Catalog[], addChildren = true): DataItemType[] => {
  const childCategories = list.filter((catalog) => catalog?.['catalog.parentid'] === id)
  return childCategories?.map((item) => {
    const subChildren = createItemType(item.id, list, addChildren)
    // [] triggers cascader to load children
    const children = isBottomCategory(item) ? undefined : []
    if (addChildren) {
      return {
        value: item['catalog.code'],
        label: item['catalog.label'],
        data: item,
        children: subChildren.length > 0 ? subChildren : undefined,
      }
    }
    return {
      value: item['catalog.code'],
      label: item['catalog.label'],
      data: item,
      children,
    }
  })
}

export const mergeDataItems = (elements: DataItemType[]): DataItemType[] => (
  elements.map((aElement, aIndex) => {
    // Find element with same code that is not the element itself
    const match = elements.find((bElement, bIndex) => (
      aElement.value === bElement.value && aIndex !== bIndex))
    if (match) {
      if (aIndex < elements.indexOf(match)) {
        // Merge if the first instance of duplicates
        return {
          ...aElement,
          children: mergeDataItems([...(aElement.children || []), ...(match.children || [])]),
        }
      }
      // Don't return second instance of duplicates, will have been merged already
      return undefined
    }
    // Return all non-duplicates normally
    return aElement
  }).filter(isDefined)
)

export const getQueryParams = (asPath: string) => (asPath.includes('?') ? `?${asPath.split('?')?.[1]}` : '')

export const arrangeSortOptions = (
  sortOptions: DataItemType<SortOptions>[],
  selectedSortOption: SortOptions,
) => [
  // To keep the selected option at top
  sortOptions.find((item) => selectedSortOption === item.value),
  ...sortOptions.filter((item) => selectedSortOption !== item.value),
].filter(isDefined)

export const getNestedCatalog = (
  catalogItem: Catalog,
  levelsDeepTarget = 100,
  currentLevel = 0,
): Catalog[] => {
  const level = currentLevel + 1
  if (level === levelsDeepTarget) return []

  return (catalogItem?.catalog
    ? catalogItem?.catalog.flatMap((catalog) => (catalog.catalog
      ? [...getNestedCatalog(catalog, levelsDeepTarget, level), catalog]
      : catalog))
    : [])
}

export const getCategoryCodeFromPath = (categoryPath?: Partial<Catalog>[]) => categoryPath?.at(-1)?.['catalog.code'] ?? ''
